import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../config/api';
import { useToast } from '../context/ToastContext';
import { useDataCache } from '../context/DataCacheContext';

export default function useChatbotList() {
  const { getCachedData, setCachedData } = useDataCache();
  const cached = getCachedData('chatbots');
  const [chatbots, setChatbots] = useState(cached || []);
  const [loading, setLoading] = useState(!cached);
  const skipNextLoadingRef = useRef(!!cached);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newChatbotName, setNewChatbotName] = useState('');
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const fetchChatbots = useCallback(async () => {
    try {
      if (!skipNextLoadingRef.current) {
        setLoading(true);
      }
      const response = await apiRequest('/chatbots/');
      if (response.ok) {
        const data = await response.json();
        setChatbots(data);
        setCachedData('chatbots', data);
      } else {
        throw new Error('Failed to fetch chatbots');
      }
    } catch (error) {
      console.error('Error fetching chatbots:', error);
      setError(error.message);
      showError('Failed to fetch chatbots');
    } finally {
      setLoading(false);
      skipNextLoadingRef.current = false;
    }
  }, [showError, setCachedData]);

  useEffect(() => {
    fetchChatbots();
  }, [fetchChatbots]);

  const handleCreateChatbot = async (e) => {
    e.preventDefault();
    if (!newChatbotName.trim()) return;

    try {
      setCreating(true);
      const response = await apiRequest('/chatbots/', {
        method: 'POST',
        body: JSON.stringify({ name: newChatbotName }),
      });

      if (!response.ok) {
        throw new Error('Failed to create chatbot');
      }

      const newChatbot = await response.json();
      setShowCreateModal(false);
      setNewChatbotName('');
      showSuccess('Chatbot created successfully');
      navigate(`/chatbots/${newChatbot.id}/builder`);
    } catch (error) {
      console.error('Error creating chatbot:', error);
      showError('Failed to create chatbot');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteChatbot = async (chatbotId) => {
    if (!window.confirm('Are you sure you want to delete this chatbot? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await apiRequest(`/chatbots/${chatbotId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setChatbots(prev => prev.filter(c => c.id !== chatbotId));
        showSuccess('Chatbot deleted successfully');
      } else {
        throw new Error('Failed to delete chatbot');
      }
    } catch (error) {
      console.error('Error deleting chatbot:', error);
      showError('Failed to delete chatbot');
    }
  };

  const handleToggleStatus = async (chatbotId, newStatus) => {
    try {
      const response = await apiRequest(`/chatbots/${chatbotId}`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus }),
      });
      if (response.ok) {
        const updated = await response.json();
        setChatbots(prev => prev.map(c => c.id === chatbotId ? { ...c, status: updated.status } : c));
        showSuccess(newStatus === 'active' ? 'Chatbot activated' : 'Chatbot deactivated');
      } else {
        throw new Error('Failed to update status');
      }
    } catch (error) {
      console.error('Error updating chatbot status:', error);
      showError('Failed to update chatbot status');
    }
  };

  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  }, []);

  return {
    chatbots,
    loading,
    error,
    showCreateModal,
    newChatbotName,
    creating,
    setShowCreateModal,
    setNewChatbotName,
    fetchChatbots,
    handleCreateChatbot,
    handleDeleteChatbot,
    handleToggleStatus,
    formatDate,
  };
}
